import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useViewMode } from './contexts/ViewModeContext';
import Header from './components/layout/Header';
import { ToastProvider } from './contexts/ToastContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { ViewModeProvider } from './contexts/ViewModeContext';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import './App.css';

// Critical pages - load immediately (homepage, auth)
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';

// Lazy load all other pages
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const EmployerPostJobPage = lazy(() => import('./pages/EmployerPostJobPage'));
const EmployerQuickPostPage = lazy(() => import('./pages/EmployerQuickPostPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EmployerApplicationsPage = lazy(() => import('./pages/EmployerApplicationsPage'));
const EmployerDashboardPage = lazy(() => import('./pages/EmployerDashboardPage'));
const AccessDeniedPage = lazy(() => import('./pages/AccessDeniedPage'));
const SavedJobsPage = lazy(() => import('./pages/SavedJobsPage'));
const CareerPlanPage = lazy(() => import('./pages/Career/CareerPlan'));
const EmployerVisaGuidePage = lazy(() => import('./pages/EmployerVisaGuidePage'));
const JobDraftsPage = lazy(() => import('./pages/JobDraftsPage'));
const EmployerJobViewPage = lazy(() => import('./pages/EmployerJobViewPage'));
const EmployerCompanyInfoPage = lazy(() => import('./pages/EmployerCompanyInfoPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const VisaDetailsPage = lazy(() => import('./pages/VisaDetailsPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const CreateBlogPage = lazy(() => import('./pages/CreateBlogPage'));
const EventsListPage = lazy(() => import('./pages/EventsListPage'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const CreateEventPage = lazy(() => import('./pages/CreateEventPage'));
const RequestDemoPage = lazy(() => import('./pages/RequestDemoPage'));
const EarlyAccessPage = lazy(() => import('./pages/EarlyAccessPage'));
const WaitlistApprovePage = lazy(() => import('./pages/WaitlistApprovePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const CareerAssistantPage = lazy(() => import('./pages/Career/CareerAssistant'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const CheckoutCancelPage = lazy(() => import('./pages/CheckoutCancelPage'));

// Loading component
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="h-8 w-8 rounded-full border-2 border-primary-600 border-b-transparent animate-spin" />
      <p className="text-sm text-slate-400 font-medium">Loading…</p>
    </div>
  </div>
);


// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-b-transparent"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/auth" replace />;
};

// Role-protected Route Component
const RoleProtectedRoute: React.FC<{ children: React.ReactNode; role: 'student' | 'employer' }> = ({ children, role }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-b-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // user?.role is expected to be 'student' or 'employer'
  if (user?.role !== role) {
    return (
      <Navigate
        to="/access-denied"
        replace
        state={{
          from: location.pathname,
          requiredRole: role,
          currentRole: user?.role ?? null,
        }}
      />
    );
  }

  return <>{children}</>;
};

// Main App Component
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const { setViewMode } = useViewMode();

  useEffect(() => {
    if (user?.role === 'employer') {
      setViewMode('employer');
    } else if (user?.role === 'student') {
      setViewMode('candidate');
    }
  }, [user?.role]);

  const location = useLocation();
  const isFullscreenPage = location.pathname === '/career/assistant';

  return (
    <div className={isFullscreenPage ? 'h-screen overflow-hidden' : 'min-h-screen bg-white flex flex-col'}>
      {!isFullscreenPage && <Header />}
      <main className={isFullscreenPage ? 'h-full' : 'flex-1'}>
        <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
          <Route
            path="/"
            element={<HomePage />}
          />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/early-access" element={<EarlyAccessPage />} />
          <Route path="/waitlist/approve" element={<WaitlistApprovePage />} />
          <Route
            path="/employer/fast-post"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerQuickPostPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/post-job"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerPostJobPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/saved"
            element={
              <RoleProtectedRoute role="student">
                <SavedJobsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/career"
            element={
              <RoleProtectedRoute role="student">
                <CareerPlanPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/applications"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerApplicationsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/dashboard"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerDashboardPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/jobs/:id"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerJobViewPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/drafts"
            element={
              <RoleProtectedRoute role="employer">
                <JobDraftsPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/employer/company"
            element={
              <RoleProtectedRoute role="employer">
                <EmployerCompanyInfoPage />
              </RoleProtectedRoute>
            }
          />
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/employer-visa-guide" element={<EmployerVisaGuidePage />} />
          <Route path="/visa-details" element={<VisaDetailsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          
          {/* Blog routes */}
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:id" element={<BlogDetailPage />} />
          <Route path="/blog/create" element={<CreateBlogPage />} />
          <Route path="/blog/edit/:id" element={<CreateBlogPage />} />

          {/* Events routes */}
          <Route path="/events" element={<EventsListPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/events/create" element={<CreateEventPage />} />
          <Route path="/events/edit/:id" element={<CreateEventPage />} />
          
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          {/** Dashboard disabled for now */}
          {/**
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } 
          />
          */}
          <Route path="*" element={<Navigate to="/" replace />} />
          <Route
            path="/career/assistant"
            element={
              <RoleProtectedRoute role="student">
                <CareerAssistantPage />
              </RoleProtectedRoute>
            }
          />
        </Routes>
        </AnimatePresence>
        </Suspense>
      </main>
      {!isFullscreenPage && <Footer />}
    </div>
  );
};

function App() {
  useEffect(() => {
    // Handle ResizeObserver errors globally
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
      }
    };

    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Scroll to top on page refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <ViewModeProvider>
        <AuthProvider>
          <ToastProvider>
            <FavoritesProvider>
              <AppContent />
            </FavoritesProvider>
          </ToastProvider>
        </AuthProvider>
      </ViewModeProvider>
    </Router>
  );
}

export default App;
